<template>
    <div
        class="NewFileListInput"
        :class="{
            'NewFileListInput--dragHover': dragHover,
            'NewFileListInput--disabled': disabled,
        }"
    >
        <label
            v-if="multiple || value.length === 0"
            class="NewFileListInput__button"
            :for="id"
            @click="openFileBrowser"
            @dragover="onDragOver"
            @dragleave="onDragLeave"
            @dragend="onDragLeave"
            @drop="onDrop"
        >
            <slot>
                <EidIcon class="NewFileListInput__buttonIcon" :icon-src="faArrowUpFromBracket" />
                <span class="NewFileListInput__buttonText">
                    <template v-if="multiple">Drag your file(s) here, or click to browse</template>
                    <template v-else>Drag your file here, or click to browse</template>
                </span>
                <span class="NewFileListInput__supportsText">Supports: {{ extensionsLabel }}</span>
            </slot>
        </label>
        <input
            :id="id"
            ref="input"
            class="NewFileListInput__input"
            type="file"
            :accept="acceptAttribute"
            :disabled="disabled"
            style="display: none"
            :multiple="multiple"
            @change="onChange"
        >
        <div class="NewFileListInput__files">
            <NewFileListInputFilePreview
                v-for="submissionFile in value"
                :key="submissionFile.file.name"
                :submission-file="submissionFile"
                :allow-remove="!disabled"
                @remove="removeFile"
            />
        </div>
    </div>
</template>

<style lang="scss">
    .NewFileListInput {
        &__button {
            display: flex;
            align-items: center;
            justify-content: center;
            flex-direction: column;
            border-radius: 3px;
            border: 2px dashed var(--eidQualityBlack-25);
            color: #2d3e50;
            font-size: 1.05em;
            padding: 20px;
            text-align: center;
            cursor: pointer;
            transition: border-color 0.2s ease, background-color 0.2s ease;
            min-height: 154px;

            &:hover {
                background-color: var(--eidTrustGreen-10);
                border-color: var(--eidTrustGreen);
            }
        }

        &__buttonIcon {
            padding: 12px;
            background-color: var(--eidTrustGreen-10);
            border-radius: 50%;
            margin-bottom: 12px;

            svg {
                color: var(--eidTrustGreen);
                height: 21px;
            }
        }

        &__buttonText {
            color: var(--eidQualityBlack);
        }

        &__supportsText {
            display: block;
            font-size: 0.72em;
            color: var(--eidQualityBlack-50);
            font-weight: 500;
            margin-top: 8px;
        }

        &__input {
            display: none;
        }

        &__files {
            .NewFileListInputFilePreview {
                margin-top: 12px;
            }
        }

        &--dragHover &__button {
            border-color: var(--eidTrustGreen);
            background: #f1faf7;
        }

        &--disabled &__button {
            opacity: 0.8;
            cursor: not-allowed;
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faArrowUpFromBracket } from '@fortawesome/pro-light-svg-icons';

    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import { cloneFile } from '@evidentid/file-utils/blobs';
    import { regularExtensions, regularMimeTypes } from '@evidentid/file-utils/mimeTypes';
    import { buildBinaryDataObjectFromFile, validateFiles } from '@evidentid/file-utils/prepareFile';

    import {
        FILE_IS_CORRUPTED_ERROR,
        FILE_SIZE_GREATER_60MB_ERROR,
        INVALID_FILE_TYPE_ERROR,
        MAX_FILE_SIZE_BYTES,
    } from './constants';
    import NewFileListInputFilePreview from './NewFileListInputFilePreview.vue';
    import { NewFileListInputFile } from './types';

    interface ValidateFilesSizeResult {
        normalSizeFiles: File[];
        tooLargeFiles: NewFileListInputFile[];
        emptyFiles: NewFileListInputFile[];
    }

    interface ValidateSuccessFilesResult {
        successSubmissionFiles: NewFileListInputFile[];
        corruptedFiles: NewFileListInputFile[];
    }

    export default defineComponent({
        name: 'NewFileListInput',
        components: {
            NewFileListInputFilePreview,
            EidIcon,
        },
        props: {
            id: {
                type: String as PropType<string | undefined>,
                default: undefined,
            },
            multiple: {
                type: Boolean as PropType<boolean>,
                default: true,
            },
            disabled: {
                type: Boolean as PropType<boolean>,
                default: false,
            },
            value: {
                type: Array as PropType<NewFileListInputFile[]>,
                default: (): NewFileListInputFile[] => [],
            },
            acceptedExtensions: {
                type: Array as PropType<string[] | null>,
                default: null,
            },
            acceptedMimeTypes: {
                type: Array as PropType<string[] | null>,
                default: null,
            },
        },
        emits: [ 'input', 'invalid' ],
        data() {
            return {
                faArrowUpFromBracket,
                dragHover: false,
            };
        },
        computed: {
            computedExtensions(): string[] {
                if (this.acceptedExtensions) {
                    return this.acceptedExtensions;
                }
                return regularExtensions.filter((extension) => extension !== 'txt');
            },
            computedMimeTypes(): string[] {
                if (this.acceptedMimeTypes) {
                    return this.acceptedMimeTypes;
                }
                return regularMimeTypes.filter((type) => type !== 'text/plain');
            },
            acceptAttribute(): string {
                return this.computedExtensions.map((extension) => `.${extension}`).join(',');
            },
            extensionsLabel(): string {
                return this.computedExtensions.map((ext) => ext.toUpperCase()).join(', ');
            },
        },
        methods: {
            openFileBrowser(event: Event): void {
                event.preventDefault();

                if (this.disabled) {
                    return;
                }

                const input = this.$refs.input as HTMLInputElement;
                if (input) {
                    input.click();
                }
            },
            removeFile(fileName: string): void {
                const filteredFiles = this.value.filter((submissionFile) => submissionFile.file.name !== fileName);
                this.$emit('input', filteredFiles);
            },
            validateFilesSize(files: FileList | File[]): ValidateFilesSizeResult {
                const normalSizeFiles: File[] = [];
                const tooLargeFiles: NewFileListInputFile[] = [];
                const emptyFiles: NewFileListInputFile[] = [];

                Array.from(files).forEach((file) => {
                    if (file.size > MAX_FILE_SIZE_BYTES) {
                        tooLargeFiles.push(this.toSubmissionFile(file, FILE_SIZE_GREATER_60MB_ERROR));
                    } else if (file.size === 0) {
                        emptyFiles.push(this.toSubmissionFile(file, FILE_IS_CORRUPTED_ERROR));
                    } else {
                        normalSizeFiles.push(file);
                    }
                });

                return { normalSizeFiles, tooLargeFiles, emptyFiles };
            },
            async validateSuccessFiles(successFiles: File[]): Promise<ValidateSuccessFilesResult> {
                const corruptedFiles: NewFileListInputFile[] = [];
                const successSubmissionFiles: NewFileListInputFile[] = [];

                for (const file of successFiles) {
                    try {
                        const binaryDataObject = await buildBinaryDataObjectFromFile(file);
                        if (binaryDataObject.data) {
                            successSubmissionFiles.push(this.toSubmissionFile(await cloneFile(file)));
                        } else {
                            corruptedFiles.push(this.toSubmissionFile(file, FILE_IS_CORRUPTED_ERROR));
                        }
                    } catch (e) {
                        corruptedFiles.push(this.toSubmissionFile(file, FILE_IS_CORRUPTED_ERROR));
                    }
                }

                return { successSubmissionFiles, corruptedFiles };
            },
            async addFiles(files: FileList | File[]): Promise<void> {
                const { normalSizeFiles, tooLargeFiles, emptyFiles } = this.validateFilesSize(files);
                const { success, failed } = validateFiles(normalSizeFiles, this.computedMimeTypes);
                const { successSubmissionFiles, corruptedFiles } = await this.validateSuccessFiles(success);

                const invalidTypeFiles = failed.map((file) => this.toSubmissionFile(file, INVALID_FILE_TYPE_ERROR));
                const nextFiles = [
                    ...successSubmissionFiles,
                    ...invalidTypeFiles,
                    ...tooLargeFiles,
                    ...emptyFiles,
                    ...corruptedFiles,
                ];

                if (nextFiles.length === 0) {
                    return;
                }

                const mergedFiles = this.mergeByFileName(nextFiles, this.value);
                this.$emit('input', mergedFiles);

                const invalidFiles = nextFiles.filter((submissionFile) => Boolean(submissionFile.error));
                if (invalidFiles.length > 0) {
                    this.$emit('invalid', invalidFiles);
                }
            },
            toSubmissionFile(file: File, error?: string | null): NewFileListInputFile {
                return error ? { file, error } : { file };
            },
            mergeByFileName(
                nextFiles: NewFileListInputFile[], currentFiles: NewFileListInputFile[],
            ): NewFileListInputFile[] {
                const merged: NewFileListInputFile[] = [];
                const seen = new Set<string>();

                [ ...nextFiles, ...currentFiles ].forEach((submissionFile) => {
                    const key = submissionFile.file.name;
                    if (!seen.has(key)) {
                        seen.add(key);
                        merged.push(submissionFile);
                    }
                });

                return merged;
            },
            onDragOver(event: Event): void {
                event.preventDefault();
                if (this.disabled) {
                    return;
                }
                this.dragHover = true;
            },
            onDragLeave(event: Event): void {
                event.preventDefault();
                this.dragHover = false;
            },
            onDrop(event: Event): void {
                event.preventDefault();
                this.dragHover = false;

                if (this.disabled) {
                    return;
                }

                const files = ((event as DragEvent).dataTransfer as DataTransfer)?.files ||
                    (event.target as HTMLInputElement)?.files;

                if (files) {
                    void this.addFiles(files);
                }
            },
            onChange(event: Event): void {
                this.onDrop(event);

                if (event.target) {
                    (event.target as HTMLInputElement).value = '';
                }
            },
        },
    });
</script>
